// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AuditSparseEntity, ProjectLineItemsList } from '@/audit/api/types';
import { AuditPropertyUpdateLabel } from '@/audit/components/audit-property-update-label';
import { AuditPropertyValue } from '@/audit/components/audit-property-value';
import {
  BillingFrequency,
  getLabelForBillingFrequency,
} from '@/constants/billing-frequency';
import { TransactionType } from '@/project/api/types';
import { Stack, Typography } from '@mui/material';

interface PropertyUpdate<Value> {
  oldValue: Value | null;
  newValue: Value | null;
}

interface LineItemEntity {
  id: string;
  key: string;
  name: string;
}

/**
 * Converts a TransactionType enum value to a human-readable label.
 */
function getLabelForTransactionType(value: TransactionType): string {
  const labels: Record<TransactionType, string> = {
    SALE: 'Sale',
    SERVICE: 'Service',
  };
  return labels[value];
}

const PRICING_MODE_LABELS: Record<string, string> = {
  PER_UNIT: 'Per unit',
  FIXED: 'Fixed',
};

const CHARGE_SCHEDULE_LABELS: Record<string, string> = {
  PERIODIC: 'Periodic',
  MILESTONE: 'Milestone',
};

const INVOICE_MODE_LABELS: Record<string, string> = {
  ADVANCE: 'In advance',
  ARREARS: 'In arrears',
};

function getLabelForEnum(labels: Record<string, string>) {
  return (value: string) => labels[value.toUpperCase()] ?? value;
}

interface LineItemUpdate {
  id: string;
  name: string;
  isAddition: boolean;
  isDeletion: boolean;
  transactionType: PropertyUpdate<TransactionType>;
  details: PropertyUpdate<string>;
  quantity: PropertyUpdate<number>;
  unitPrice: PropertyUpdate<string>;
  item: PropertyUpdate<LineItemEntity>;
  location: PropertyUpdate<AuditSparseEntity>;
  billingFrequency: PropertyUpdate<BillingFrequency>;
  startOn: PropertyUpdate<string>;
  endOn: PropertyUpdate<string>;
  pricingMode: PropertyUpdate<string>;
  chargeSchedule: PropertyUpdate<string>;
  chargeFrequency: PropertyUpdate<string>;
  invoiceFrequency: PropertyUpdate<string>;
  invoiceMode: PropertyUpdate<string>;
  fixedPrice: PropertyUpdate<string>;
  totalOverride: PropertyUpdate<string>;
}

interface Props {
  property: ProjectLineItemsList;
}

/**
 * Builds a display name for a line item, appending "(Phase N)" when
 * the item is part of a phased group (multiple items share the same
 * aggregationKey across old and new values).
 */
function getItemDisplayName(
  itemName: string,
  aggregationKey: string | undefined,
  phaseRowNumber: number | undefined,
  allItems: { aggregationKey?: string }[]
): string {
  if (!aggregationKey || phaseRowNumber === undefined) return itemName;
  const groupCount = allItems.filter(
    (item) => item.aggregationKey === aggregationKey
  ).length;
  if (groupCount <= 1) return itemName;
  return `${itemName} (Phase ${String(phaseRowNumber)})`;
}

export function AuditProjectLineItemsListLabel(props: Props) {
  const updates: LineItemUpdate[] = [];

  // Combined list of all items for phase detection, deduplicated by id so
  // an item appearing in both old and new doesn't inflate the phase group count
  const allItems = [
    ...props.property.oldValue,
    ...props.property.newValue,
  ].filter(
    (item, index, arr) =>
      arr.findIndex((other) => other.id === item.id) === index
  );

  props.property.oldValue.forEach((lineItem) => {
    const newLineItem = props.property.newValue.find(({ id }) => {
      return id === lineItem.id;
    });

    const isDeletion = newLineItem === undefined;
    const details = newLineItem?.details ?? null;
    const quantity = newLineItem?.quantity ?? null;
    const unitPrice = newLineItem?.unitPrice ?? null;
    const billingFrequency = newLineItem?.billingFrequency ?? null;
    const transactionType = newLineItem?.transactionType ?? null;
    const location = newLineItem?.location ?? null;
    const startOn = newLineItem?.startOn ?? null;
    const endOn = newLineItem?.endOn ?? null;

    updates.push({
      isDeletion,
      id: lineItem.id,
      name: getItemDisplayName(
        lineItem.item?.name ?? 'Unknown',
        lineItem.aggregationKey,
        lineItem.phaseRowNumber,
        allItems
      ),
      isAddition: false,
      transactionType: {
        oldValue: lineItem.transactionType ?? null,
        newValue: transactionType,
      },
      details: { oldValue: lineItem.details, newValue: details },
      quantity: { oldValue: lineItem.quantity, newValue: quantity },
      unitPrice: { oldValue: lineItem.unitPrice ?? null, newValue: unitPrice },
      item: { oldValue: lineItem.item, newValue: newLineItem?.item ?? null },
      location: { oldValue: lineItem.location ?? null, newValue: location },
      billingFrequency: {
        oldValue: lineItem.billingFrequency,
        newValue: billingFrequency,
      },
      startOn: { oldValue: lineItem.startOn ?? null, newValue: startOn },
      endOn: { oldValue: lineItem.endOn ?? null, newValue: endOn },
      pricingMode: {
        oldValue: lineItem.pricingMode ?? null,
        newValue: newLineItem?.pricingMode ?? null,
      },
      chargeSchedule: {
        oldValue: lineItem.chargeSchedule ?? null,
        newValue: newLineItem?.chargeSchedule ?? null,
      },
      chargeFrequency: {
        oldValue: lineItem.chargeFrequency ?? null,
        newValue: newLineItem?.chargeFrequency ?? null,
      },
      invoiceFrequency: {
        oldValue: lineItem.invoiceFrequency ?? null,
        newValue: newLineItem?.invoiceFrequency ?? null,
      },
      invoiceMode: {
        oldValue: lineItem.invoiceMode ?? null,
        newValue: newLineItem?.invoiceMode ?? null,
      },
      fixedPrice: {
        oldValue: lineItem.fixedPrice ?? null,
        newValue: newLineItem?.fixedPrice ?? null,
      },
      totalOverride: {
        oldValue: lineItem.totalOverride ?? null,
        newValue: newLineItem?.totalOverride ?? null,
      },
    });
  });

  props.property.newValue.forEach((lineItem) => {
    const update = updates.find((update) => update.id === lineItem.id);

    if (update === undefined) {
      return updates.push({
        id: lineItem.id,
        name: getItemDisplayName(
          lineItem.item?.name ?? 'Unknown',
          lineItem.aggregationKey,
          lineItem.phaseRowNumber,
          allItems
        ),
        isAddition: true,
        isDeletion: false,
        transactionType: {
          oldValue: null,
          newValue: lineItem.transactionType ?? null,
        },
        details: { oldValue: null, newValue: lineItem.details },
        quantity: { oldValue: null, newValue: lineItem.quantity },
        unitPrice: { oldValue: null, newValue: lineItem.unitPrice ?? null },
        item: { oldValue: null, newValue: lineItem.item },
        location: { oldValue: null, newValue: lineItem.location ?? null },
        billingFrequency: {
          oldValue: null,
          newValue: lineItem.billingFrequency,
        },
        startOn: { oldValue: null, newValue: lineItem.startOn ?? null },
        endOn: { oldValue: null, newValue: lineItem.endOn ?? null },
        pricingMode: { oldValue: null, newValue: lineItem.pricingMode ?? null },
        chargeSchedule: {
          oldValue: null,
          newValue: lineItem.chargeSchedule ?? null,
        },
        chargeFrequency: {
          oldValue: null,
          newValue: lineItem.chargeFrequency ?? null,
        },
        invoiceFrequency: {
          oldValue: null,
          newValue: lineItem.invoiceFrequency ?? null,
        },
        invoiceMode: { oldValue: null, newValue: lineItem.invoiceMode ?? null },
        fixedPrice: { oldValue: null, newValue: lineItem.fixedPrice ?? null },
        totalOverride: {
          oldValue: null,
          newValue: lineItem.totalOverride ?? null,
        },
      });
    }
  });

  let noOps = 0;
  const renderedOut = (
    <Stack>
      <Typography component="span" variant="caption" fontWeight="bold">
        {props.property.name}:
      </Typography>
      {updates.map((update) => {
        const itemLabel = 'Item';

        if (update.isAddition) {
          return (
            <Stack key={update.id}>
              <Typography variant="caption" marginLeft={2}>
                <Typography component="span" variant="inherit">
                  {update.name}
                </Typography>{' '}
                <Typography
                  component="span"
                  variant="inherit"
                  sx={{ textDecoration: 'underline' }}
                >
                  added
                </Typography>
              </Typography>
              <Typography variant="caption" marginLeft={4}>
                {itemLabel}:&nbsp;
                <AuditPropertyValue
                  value={update.item.newValue?.name ?? null}
                />
              </Typography>
              <Typography variant="caption" marginLeft={4}>
                Transaction type:&nbsp;
                <AuditPropertyValue
                  value={update.transactionType.newValue}
                  getLabelForValue={getLabelForTransactionType}
                />
              </Typography>
              <Typography variant="caption" marginLeft={4}>
                Quantity:&nbsp;
                <AuditPropertyValue value={update.quantity.newValue} />
              </Typography>
              <Typography variant="caption" marginLeft={4}>
                Unit price:&nbsp;
                <AuditPropertyValue value={update.unitPrice.newValue} />
              </Typography>
              <Typography variant="caption" marginLeft={4}>
                Frequency:&nbsp;
                <AuditPropertyValue
                  value={update.billingFrequency.newValue}
                  getLabelForValue={getLabelForBillingFrequency}
                />
              </Typography>
              <Typography variant="caption" marginLeft={4}>
                Location:&nbsp;
                <AuditPropertyValue
                  value={update.location.newValue?.name ?? null}
                />
              </Typography>
              <Typography variant="caption" marginLeft={4}>
                Details:&nbsp;
                <AuditPropertyValue value={update.details.newValue} />
              </Typography>
              {update.startOn.newValue != null && (
                <Typography variant="caption" marginLeft={4}>
                  Start date:&nbsp;
                  <AuditPropertyValue value={update.startOn.newValue} />
                </Typography>
              )}
              {update.endOn.newValue != null && (
                <Typography variant="caption" marginLeft={4}>
                  End date:&nbsp;
                  <AuditPropertyValue value={update.endOn.newValue} />
                </Typography>
              )}
            </Stack>
          );
        }

        if (update.isDeletion) {
          return (
            <Stack key={update.id}>
              <Typography variant="caption" marginLeft={2}>
                <Typography component="span" variant="inherit">
                  {update.name}
                </Typography>{' '}
                <Typography
                  component="span"
                  variant="inherit"
                  sx={{ textDecoration: 'underline' }}
                >
                  removed
                </Typography>
              </Typography>
              <Typography variant="caption" marginLeft={4}>
                {itemLabel}:&nbsp;
                <AuditPropertyValue
                  value={update.item.oldValue?.name ?? null}
                />
              </Typography>
              <Typography variant="caption" marginLeft={4}>
                Transaction type:&nbsp;
                <AuditPropertyValue
                  value={update.transactionType.oldValue}
                  getLabelForValue={getLabelForTransactionType}
                />
              </Typography>
              <Typography variant="caption" marginLeft={4}>
                Quantity:&nbsp;
                <AuditPropertyValue value={update.quantity.oldValue} />
              </Typography>
              <Typography variant="caption" marginLeft={4}>
                Unit price:&nbsp;
                <AuditPropertyValue value={update.unitPrice.oldValue} />
              </Typography>
              <Typography variant="caption" marginLeft={4}>
                Frequency:&nbsp;
                <AuditPropertyValue
                  value={update.billingFrequency.oldValue}
                  getLabelForValue={getLabelForBillingFrequency}
                />
              </Typography>
              <Typography variant="caption" marginLeft={4}>
                Location:&nbsp;
                <AuditPropertyValue
                  value={update.location.oldValue?.name ?? null}
                />
              </Typography>
              <Typography variant="caption" marginLeft={4}>
                Details:&nbsp;
                <AuditPropertyValue value={update.details.oldValue} />
              </Typography>
              {update.startOn.oldValue != null && (
                <Typography variant="caption" marginLeft={4}>
                  Start date:&nbsp;
                  <AuditPropertyValue value={update.startOn.oldValue} />
                </Typography>
              )}
              {update.endOn.oldValue != null && (
                <Typography variant="caption" marginLeft={4}>
                  End date:&nbsp;
                  <AuditPropertyValue value={update.endOn.oldValue} />
                </Typography>
              )}
            </Stack>
          );
        }

        const showQuantity =
          update.quantity.oldValue !== update.quantity.newValue;

        const showItem = update.item.oldValue?.id !== update.item.newValue?.id;

        const showTransactionType =
          update.transactionType.oldValue !== update.transactionType.newValue;

        const showUnitPrice =
          update.unitPrice.oldValue !== update.unitPrice.newValue;

        const showFrequency =
          update.billingFrequency.oldValue !== update.billingFrequency.newValue;

        const showLocation =
          update.location.oldValue?.id !== update.location.newValue?.id;

        const showDetails = update.details.oldValue !== update.details.newValue;

        const showStartOn = update.startOn.oldValue !== update.startOn.newValue;

        const showEndOn = update.endOn.oldValue !== update.endOn.newValue;

        const showPricingMode =
          update.pricingMode.oldValue !== update.pricingMode.newValue;

        const showChargeSchedule =
          update.chargeSchedule.oldValue !== update.chargeSchedule.newValue;

        const showChargeFrequency =
          update.chargeFrequency.oldValue !== update.chargeFrequency.newValue;

        const showInvoiceFrequency =
          update.invoiceFrequency.oldValue !== update.invoiceFrequency.newValue;

        const showInvoiceMode =
          update.invoiceMode.oldValue !== update.invoiceMode.newValue;

        const showFixedPrice =
          update.fixedPrice.oldValue !== update.fixedPrice.newValue;

        const showTotalOverride =
          update.totalOverride.oldValue !== update.totalOverride.newValue;

        if (
          !showQuantity &&
          !showUnitPrice &&
          !showFrequency &&
          !showLocation &&
          !showDetails &&
          !showItem &&
          !showTransactionType &&
          !showStartOn &&
          !showEndOn &&
          !showPricingMode &&
          !showChargeSchedule &&
          !showChargeFrequency &&
          !showInvoiceFrequency &&
          !showInvoiceMode &&
          !showFixedPrice &&
          !showTotalOverride
        ) {
          noOps++;
          return null;
        }

        return (
          <Stack key={update.id}>
            <Typography variant="caption" marginLeft={2}>
              <Typography component="span" variant="inherit">
                {update.name}
              </Typography>{' '}
              <Typography
                component="span"
                variant="inherit"
                sx={{ textDecoration: 'underline' }}
              >
                updated
              </Typography>
            </Typography>
            {showItem && (
              <Typography variant="caption" component="div" marginLeft={4}>
                <AuditPropertyUpdateLabel
                  name={itemLabel}
                  oldValue={update.item.oldValue?.name ?? null}
                  newValue={update.item.newValue?.name ?? null}
                />
              </Typography>
            )}
            {showTransactionType && (
              <Typography variant="caption" component="div" marginLeft={4}>
                <AuditPropertyUpdateLabel
                  name="Transaction type"
                  oldValue={update.transactionType.oldValue}
                  newValue={update.transactionType.newValue}
                  getLabelForValue={getLabelForTransactionType}
                />
              </Typography>
            )}
            {showQuantity && (
              <Typography variant="caption" component="div" marginLeft={4}>
                <AuditPropertyUpdateLabel
                  name="Quantity"
                  oldValue={update.quantity.oldValue}
                  newValue={update.quantity.newValue}
                />
              </Typography>
            )}
            {showUnitPrice && (
              <Typography variant="caption" component="div" marginLeft={4}>
                <AuditPropertyUpdateLabel
                  name="Unit price"
                  oldValue={update.unitPrice.oldValue}
                  newValue={update.unitPrice.newValue}
                />
              </Typography>
            )}
            {showFrequency && (
              <Typography variant="caption" component="div" marginLeft={4}>
                <AuditPropertyUpdateLabel
                  name="Frequency"
                  oldValue={update.billingFrequency.oldValue}
                  newValue={update.billingFrequency.newValue}
                  getLabelForValue={getLabelForBillingFrequency}
                />
              </Typography>
            )}
            {showLocation && (
              <Typography variant="caption" component="div" marginLeft={4}>
                <AuditPropertyUpdateLabel
                  name="Location"
                  oldValue={update.location.oldValue?.name ?? null}
                  newValue={update.location.newValue?.name ?? null}
                />
              </Typography>
            )}
            {showDetails && (
              <Typography variant="caption" component="div" marginLeft={4}>
                <AuditPropertyUpdateLabel
                  name="Details"
                  oldValue={update.details.oldValue}
                  newValue={update.details.newValue}
                />
              </Typography>
            )}
            {showStartOn && (
              <Typography variant="caption" component="div" marginLeft={4}>
                <AuditPropertyUpdateLabel
                  name="Start date"
                  oldValue={update.startOn.oldValue}
                  newValue={update.startOn.newValue}
                />
              </Typography>
            )}
            {showEndOn && (
              <Typography variant="caption" component="div" marginLeft={4}>
                <AuditPropertyUpdateLabel
                  name="End date"
                  oldValue={update.endOn.oldValue}
                  newValue={update.endOn.newValue}
                />
              </Typography>
            )}
            {showPricingMode && (
              <Typography variant="caption" component="div" marginLeft={4}>
                <AuditPropertyUpdateLabel
                  name="Pricing mode"
                  oldValue={update.pricingMode.oldValue}
                  newValue={update.pricingMode.newValue}
                  getLabelForValue={getLabelForEnum(PRICING_MODE_LABELS)}
                />
              </Typography>
            )}
            {showChargeSchedule && (
              <Typography variant="caption" component="div" marginLeft={4}>
                <AuditPropertyUpdateLabel
                  name="Charge schedule"
                  oldValue={update.chargeSchedule.oldValue}
                  newValue={update.chargeSchedule.newValue}
                  getLabelForValue={getLabelForEnum(CHARGE_SCHEDULE_LABELS)}
                />
              </Typography>
            )}
            {showChargeFrequency && (
              <Typography variant="caption" component="div" marginLeft={4}>
                <AuditPropertyUpdateLabel
                  name="Charge frequency"
                  oldValue={update.chargeFrequency.oldValue}
                  newValue={update.chargeFrequency.newValue}
                  getLabelForValue={getLabelForBillingFrequency}
                />
              </Typography>
            )}
            {showInvoiceFrequency && (
              <Typography variant="caption" component="div" marginLeft={4}>
                <AuditPropertyUpdateLabel
                  name="Invoice frequency"
                  oldValue={update.invoiceFrequency.oldValue}
                  newValue={update.invoiceFrequency.newValue}
                  getLabelForValue={getLabelForBillingFrequency}
                />
              </Typography>
            )}
            {showInvoiceMode && (
              <Typography variant="caption" component="div" marginLeft={4}>
                <AuditPropertyUpdateLabel
                  name="Invoice mode"
                  oldValue={update.invoiceMode.oldValue}
                  newValue={update.invoiceMode.newValue}
                  getLabelForValue={getLabelForEnum(INVOICE_MODE_LABELS)}
                />
              </Typography>
            )}
            {showFixedPrice && (
              <Typography variant="caption" component="div" marginLeft={4}>
                <AuditPropertyUpdateLabel
                  name="Fixed price"
                  oldValue={update.fixedPrice.oldValue}
                  newValue={update.fixedPrice.newValue}
                />
              </Typography>
            )}
            {showTotalOverride && (
              <Typography variant="caption" component="div" marginLeft={4}>
                <AuditPropertyUpdateLabel
                  name="Total override"
                  oldValue={update.totalOverride.oldValue}
                  newValue={update.totalOverride.newValue}
                />
              </Typography>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
  if (noOps === updates.length) {
    // If nothing changed, just don't show this
    return null;
  }
  return renderedOut;
}
